import React from 'react';
import { useTranslation } from 'react-i18next';
import { RangeFacet } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/response/GetIAMReferencesResponse';
import { Checkbox, Flex, InputNumber, MarginBox, Text } from 'UI';
import { isDefaultRange, UsePrice } from '../Filter.shared';
import { FILTER_PRICES, FiltersRange, RangeFacetLocal, TextFacetLocal } from '../Filter.types';
import DebouncedSlider from './FilterDebouncedSlider';

const inputValidationRegex = (val: string) => !/[^0-9.,]/.test(val) && (val.match(/[.,]/g) ?? []).length <= 1;
interface RangeFilterProps {
  min: number;
  max: number;
  isInputsDisabled: boolean;
  isSliderDisabled: boolean;
  facet: TextFacetLocal | RangeFacetLocal;
  filterValues: [number, number];
  setRangeFilterImmediate: (id: string, range?: FiltersRange) => void;
  setRangeFilterDebounced: (id: string, range?: FiltersRange) => void;
  usePrice?: UsePrice;
  inline?: boolean;
}

const RangeFilter = ({
  min,
  max,
  isInputsDisabled,
  isSliderDisabled,
  facet,
  filterValues,
  setRangeFilterImmediate,
  setRangeFilterDebounced,
  usePrice,
  inline,
}: RangeFilterProps) => {
  const { t } = useTranslation();

  return (
    <Flex direction={'column'} maxWidth={inline ? 260 : 'auto'}>
      <DebouncedSlider
        min={min}
        max={max}
        filterValues={filterValues}
        facet={facet as RangeFacet}
        isSliderDisabled={isSliderDisabled}
        setRangeFilter={setRangeFilterImmediate}
      />
      <Flex>
        <Flex>
          <InputNumber
            value={filterValues[0]}
            disabled={isInputsDisabled}
            min={min}
            max={max}
            validateInput={inputValidationRegex}
            onBlur={(val) => {
              if (val == null) {
                return;
              }
              const newMax = Math.max(filterValues[1], val);
              if (isDefaultRange(val, newMax, min, max)) {
                setRangeFilterDebounced(facet.id);
              } else {
                setRangeFilterDebounced(facet.id, { min: val, max: newMax });
              }
            }}
          />
        </Flex>
        <Flex />
        <Flex>
          <InputNumber
            value={filterValues[1]}
            disabled={isInputsDisabled}
            min={min}
            max={max}
            validateInput={inputValidationRegex}
            onBlur={(val) => {
              if (val == null) {
                return;
              }
              const newMin = Math.min(filterValues[0], val);
              if (isDefaultRange(newMin, val, min, max)) {
                setRangeFilterDebounced(facet.id);
              } else {
                setRangeFilterDebounced(facet.id, { min: newMin, max: val });
              }
            }}
          />
        </Flex>
      </Flex>
      {facet.id === FILTER_PRICES && usePrice && (
        <MarginBox mt={25}>
          <Checkbox
            onChange={() => usePrice.setShowPriceless((prev) => !prev)}
            checked={usePrice.showPriceless}
            label={
              <Text type={'text'}>
                {t('catalog.universal_products.filter.include_items_without_prices', 'Include items without prices')}
              </Text>
            }
          />
        </MarginBox>
      )}
    </Flex>
  );
};

export default RangeFilter;
